import { useRef, useState, type FormEvent } from "react";
import { normalizeRelativePath } from "../domain/paths";
import { Modal } from "./modal";

export interface CreateFolderDialogProps {
  readonly onClose: () => void;
  readonly onCreate: (name: string) => Promise<void>;
}

/** Collects one validated root folder name and surfaces safe collision failures. */
export function CreateFolderDialog({ onClose, onCreate }: CreateFolderDialogProps) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /** Creates the folder only after explicit form submission. */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const normalized = normalizeRelativePath([name])[0];
      if (normalized === undefined) {
        throw new Error("Enter a folder name.");
      }
      await onCreate(normalized);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The folder could not be created.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Create folder"
      titleId="create-folder-title"
      initialFocusRef={inputRef}
      onClose={onClose}
    >
      <form className="management-dialog" onSubmit={(event) => void handleSubmit(event)}>
        <label>
          Folder name
          <input
            ref={inputRef}
            required
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
        </label>
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <footer className="modal__actions">
          <button type="button" className="secondary-action" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={busy}>
            Create Folder
          </button>
        </footer>
      </form>
    </Modal>
  );
}
